import axios from "axios";
import { API_BASE_URL } from "@/config";
import { ApiService, type ApiRequestConfig } from "./api.service";
import type { components } from "@/types/api.generated";

/** One of the signed-in support identity's live temporary-access grants (NG-3417, D9). */
export type MySupportGrant = components["schemas"]["MySupportGrant"];

/**
 * Temporary access, the product's side (NG-3417, Phase 2 D9): the handoff from the Backoffice, the
 * banner's read and Leave. Every call is the support identity's own; for anyone else the server
 * answers the read with `[]` and the rest with an error.
 */
export class SupportAccessService extends ApiService {
  /** Exchanges the Backoffice's single-use handoff code for a product session. Resolves with the
   *  workspace's slug; rejects with a 400 for a used, expired or unknown code.
   *
   *  Deliberately a bare axios call, not the shared client: the shared client serialises a failed
   *  request's body into Sentry with only secret-NAMED fields redacted, and this body's one field
   *  is the credential, named `code`. Here nothing logs the request; the caller logs only a
   *  status summary (`requestErrorSummary`). */
  async enter(code: string): Promise<string> {
    const response = await axios.post<components["schemas"]["SupportAccessEntered"]>(
      `${API_BASE_URL}/auth/support/enter/`,
      { code },
      { withCredentials: true, headers: { "Content-Type": "application/json" } },
    );
    return response.data.workspace_slug;
  }

  /** The caller's live grants, soonest end first. `config` carries a query's abort signal. */
  async fetchMyGrants(config?: ApiRequestConfig): Promise<MySupportGrant[]> {
    return this.get<MySupportGrant[]>("/api/users/me/support-grants/", config);
  }

  /** Ends the caller's own grant. */
  async leave(grantId: string): Promise<void> {
    await this.post(`/api/users/me/support-grants/${grantId}/leave/`);
  }
}
